import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { fetch, optimisticUpdate, pessimisticUpdate } from '@ngrx/router-store/data-persistence';
import { Store } from '@ngrx/store';
import { undo } from 'ngrx-undo-v2';
import { from } from 'rxjs';
import { map, mapTo } from 'rxjs/operators';
import { SchoolBillingAddressQueries } from '.';
import {
  SCHOOL_BILLING_ADDRESS_SERVICE_TOKEN,
  SchoolBillingAddressServiceInterface,
} from '../../school-billing-address/school-billing-address.service.interface';
import { ActionSuccessful } from '../dal.actions';
import { DalState } from '../dal.state.interface';
import { EffectFeedback } from '../effect-feedback';
import { AddEffectFeedback } from '../effect-feedback/effect-feedback.actions';
import {
  AddSchoolBillingAddress,
  DeleteSchoolBillingAddress,
  LoadSchoolBillingAddress,
  SchoolBillingAddressActionTypes,
  SchoolBillingAddressLoaded,
  SchoolBillingAddressLoadError,
  StartAddSchoolBillingAddress,
  UpdateSchoolBillingAddress,
} from './school-billing-address.actions';

@Injectable()
export class SchoolBillingAddressEffects {
  loadSchoolBillingAddress$ = createEffect(() =>
    this.actions.pipe(
      ofType(SchoolBillingAddressActionTypes.LoadSchoolBillingAddress),
      concatLatestFrom(() => this.store.select(SchoolBillingAddressQueries.getLoaded)),
      fetch({
        run: (action: LoadSchoolBillingAddress, loaded: boolean) => {
          if (!action.payload.force && loaded) return;
          return this.schoolBillingAddressService
            .getAllForUser(action.payload.userId)
            .pipe(map((schoolBillingAddresses) => new SchoolBillingAddressLoaded({ schoolBillingAddresses })));
        },
        onError: (action: LoadSchoolBillingAddress, error) => {
          return new SchoolBillingAddressLoadError(error);
        },
      })
    )
  );

  startAddSchoolBillingAddress$ = createEffect(() =>
    this.actions.pipe(
      ofType(SchoolBillingAddressActionTypes.StartAddSchoolBillingAddress),
      pessimisticUpdate({
        run: (action: StartAddSchoolBillingAddress) => {
          return this.schoolBillingAddressService
            .createSchoolBillingAddressForSchool(action.payload.schoolId, {
              ...action.payload.schoolBillingAddress,
            })
            .pipe(map((schoolBillingAddress) => new AddSchoolBillingAddress({ schoolBillingAddress })));
        },
        onError: (action: StartAddSchoolBillingAddress, error) => {
          return new AddEffectFeedback({
            effectFeedback: EffectFeedback.generateErrorFeedback(
              this.uuid(),
              action,
              'Het is niet gelukt om een adres aan te maken'
            ),
          });
        },
      })
    )
  );

  updateSchoolBillingAddress$ = createEffect(() =>
    this.actions.pipe(
      ofType(SchoolBillingAddressActionTypes.UpdateSchoolBillingAddress),
      optimisticUpdate({
        run: (action: UpdateSchoolBillingAddress) => {
          return this.schoolBillingAddressService
            .updateSchoolBillingAddressForSchool(
              action.payload.schoolId,
              +action.payload.schoolBillingAddress.id,
              action.payload.schoolBillingAddress.changes
            )
            .pipe(mapTo(new ActionSuccessful({ successfulAction: '' })));
        },
        undoAction: (action: UpdateSchoolBillingAddress, error) => {
          const errorFeedbackAction = new AddEffectFeedback({
            effectFeedback: EffectFeedback.generateErrorFeedback(
              this.uuid(),
              action,
              'Het is niet gelukt om het adres bij te werken.'
            ),
          });
          return from([undo(action), errorFeedbackAction]);
        },
      })
    )
  );

  deleteSchoolBillingAddress$ = createEffect(() =>
    this.actions.pipe(
      ofType(SchoolBillingAddressActionTypes.DeleteSchoolBillingAddress),
      optimisticUpdate({
        run: (action: DeleteSchoolBillingAddress) => {
          const { id, schoolId } = action.payload;
          return this.schoolBillingAddressService
            .deleteSchoolBillingAddressForSchool(schoolId, id)
            .pipe(mapTo(new ActionSuccessful({ successfulAction: '' })));
        },
        undoAction: (action: DeleteSchoolBillingAddress, error) => {
          const errorFeedbackAction = new AddEffectFeedback({
            effectFeedback: EffectFeedback.generateErrorFeedback(
              this.uuid(),
              action,
              'Het is niet gelukt om het adres te verwijderen.'
            ),
          });
          return from([undo(action), errorFeedbackAction]);
        },
      })
    )
  );

  constructor(
    @Inject('uuid') private uuid: () => string,
    private store: Store<DalState>,
    private actions: Actions,
    @Inject(SCHOOL_BILLING_ADDRESS_SERVICE_TOKEN)
    private schoolBillingAddressService: SchoolBillingAddressServiceInterface
  ) {}
}
